<script setup lang="ts">
import ArchetypePage from '~/components/archetypes/ArchetypePage.vue';

useSeoMeta({
  title: 'Controlled Change for GMP AI Workflows — QikSolve',
  description:
    'How GMP teams can assess AI governance changes to models, prompts, retrieval, tools, configurations, and AI workflows under controlled review.',
});

const page = {
  hero: {
    eyebrow: 'Controlled change for GMP AI',
    title: 'Treat AI workflow change as a quality decision',
    summary:
      'Changes to models, prompts, retrieval, rules, tools, and workflows can alter behaviour and evidence. A proportionate AI governance change path keeps learning visible and reviewable.',
    cta: { label: 'Discuss your GMP AI pathway', to: '/contact' },
  },
  intro: {
    title: 'Change is more than a code deployment',
    content:
      'In a governed AI workflow, behaviour can change when the model, prompt, retrieval sources, rules, tools, thresholds, or surrounding process changes. The team needs a way to decide what the change means for quality and evidence.',
    items: [
      { title: 'Identify the change', description: 'Describe what changed and which process, output, control, or evidence path may be affected.' },
      { title: 'Assess impact', description: 'Consider intended use, output class, review requirements, data integrity, and validated-state implications.' },
      { title: 'Decide the response', description: 'Define approval, evaluation, rollback, monitoring, or revalidation actions proportionate to the risk.' },
    ],
  },
  sections: [
    {
      title: 'Changes worth assessing explicitly',
      items: [
        { title: 'Model or deployment changes', description: 'A new model version or runtime can change output behaviour and evaluation results.' },
        { title: 'Prompt and policy changes', description: 'Instructions, policies, and guardrails can alter what the workflow produces or permits.' },
        { title: 'Retrieval and reference changes', description: 'New, removed, or changed source material can affect evidence, recommendations, and reviewer context.' },
        { title: 'Tool and workflow changes', description: 'New permissions, actions, thresholds, retries, or handoffs can change process impact.' },
      ],
    },
    {
      title: 'A controlled response',
      items: [
        { title: 'Classify the change', description: 'Use impact and output boundary to decide the review depth.' },
        { title: 'Evaluate and approve', description: 'Run the defined checks, record the decision, and retain the relevant evidence.' },
        { title: 'Monitor after release', description: 'Watch quality, exceptions, review outcomes, and escalation signals after the change.' },
      ],
    },
  ],
  callout: {
    title: 'Controlled change supports better learning',
    content: 'A clear change path helps GMP teams improve AI-assisted work without losing the quality context, evidence, or accountability that makes the workflow trustworthy.',
    tone: 'boundary' as const,
  },
  relatedLinks: {
    title: 'Continue the decision',
    items: [
      { label: 'Practical, governed AI hub', description: 'Return to the full Pillar 5 governance pathway.', to: '/product/agentic-ai-governance' },
      { label: 'Evaluate AI before and after release', description: 'Define evaluation, monitoring, and effectiveness review.', to: '/product/evaluating-gmp-ai-before-and-after-release' },
      { label: 'Evidence and traceability', description: 'Keep change and review context understandable and retrievable.', to: '/product/evidence-and-traceability-for-gmp-ai' },
    ],
  },
  cta: { title: 'Have a change to assess?', content: 'Bring the model, prompt, retrieval, tool, or workflow change to a practical governance discussion.', label: 'Discuss your pathway', to: '/contact' },
};
</script>

<template>
  <ArchetypePage v-bind="page" />
</template>
